import { urlToURL } from "../../lib/utils/domains";
import { fileNameSafeString } from "../../lib/utils/fileNameSafeString";
import { DataGoesWhereEventArchiveFormat, DbRecording, eventsFileFormatVersion, UUID } from "./DatabaseTypes";
import { DbEventsTableReader } from "./DbEventsTableReader";
import { DbMhtmlCaptureTableReader } from "./DbMhtmlCaptureTableReader";
import { DbVideoFragmentTableReader } from "./DbVideoFragmentTableReader";
import { ExtensionSettings, getDirectoryToSaveInto } from "./DbExtensionSettings";
import { DbRecordingEntryReader } from "./DbRecordingEntryReader";

export const recordingToArchive = async (recordingUuid: UUID) => {
	const recordingReader = await DbRecordingEntryReader.fromRecordingUuid(recordingUuid);
	if (recordingReader == null) return;
	const {recording} = recordingReader;
	const events = await DbEventsTableReader.forRecordingUuid(recordingUuid).getEvents();
	return {
		version: eventsFileFormatVersion,
		...recording,
		events,
	} satisfies DataGoesWhereEventArchiveFormat;
}

export const writeRecordingToArchiveFile = async (
	directoryHandle: FileSystemDirectoryHandle,
	baseName: string,
	recording: DbRecording,
) => {
	const fileSystemHandle = await directoryHandle.getFileHandle(`${fileNameSafeString(baseName)} Events.json`,{ create: true });
	const writeStream = await fileSystemHandle.createWritable({ keepExistingData: false });

	const eventsFileFormatHeader = `{
	"version": ${eventsFileFormatVersion},${
			recording.name == null ? '' : `
	"name": ${JSON.stringify(recording.name)},`}
	"searchTermsNewlineSeparated": ${JSON.stringify(recording.searchTermsNewlineSeparated ?? "", undefined, "\t").replaceAll("\n", "\n\t")},
	"globalSearchTermsNewlineSeparated": ${JSON.stringify(recording.globalSearchTermsNewlineSeparated ?? "", undefined, "\t").replaceAll("\n", "\n\t")},
	"recordingUuid": ${JSON.stringify(recording.recordingUuid)},
	"startTimeIso": ${JSON.stringify(recording.startTimeIso)},
	"settings": ${JSON.stringify(recording.settings, undefined, "\t").replaceAll("\n", "\n\t\t")},
	"dataSummary": ${JSON.stringify(recording.dataSummary, undefined, "\t").replaceAll("\n", "\n\t\t")},
	"events": [`
	const eventsFileFormatFooter = `]\n}\n`;
	
	await writeStream.write(eventsFileFormatHeader);
	let first = true;
	await DbEventsTableReader.forRecordingUuid(recording.recordingUuid).forEventsAsync(
		async (event) => {
			await writeStream.write(
				// add comma separator before all events but the first
				(first ? '' : ",") +
				// stringify using tab indentation
				JSON.stringify(replaceArrayBuffersWithBase64(event), null, "\t")
					// shift stringified result two tabs to the right since
					// there are two levels of indentation in the file format
					.replaceAll("\n", "\n\t")
			);
			first = false;
		}
	);
	await writeStream.write(eventsFileFormatFooter);
	await writeStream.close();
}

export const importRecordingAndDbEventsFromArchive = async (
	archive: DataGoesWhereEventArchiveFormat,
	onProgressUpdate?: (percentDone: number) => void
) => {
	const {version, events, ...dbRecording} = archive;
	const eventsRemaining = [...events];
	onProgressUpdate?.(0);
	const db = await getDatabase();
	if (db == null) return;
	await db.put("recordings", dbRecording);
	onProgressUpdate?.(1);
	let written = 0;
	const chunkSize = 1000;
	while (eventsRemaining.length > 0) {
		const chunk = eventsRemaining.splice(0, chunkSize);
		const tx = db.transaction("events", "readwrite", {durability: "relaxed"});
		await Promise.all( chunk.map( chunk => tx.store.put(chunk) ) );
		tx.commit();
		written += chunk.length;
		onProgressUpdate?.(1 + Math.floor(99 * written / events.length));
	}
	
	// await Promise.all(
	// 	events.map( event => db.put("events", event) )
	// );
	onProgressUpdate?.(100);
	RecordingCreated.send({recordingUuid: dbRecording.recordingUuid});
	console.log("Wrote events", eventsRemaining.length);
}

export const writeVideoStreamsToFiles = async (
	directoryHandle: FileSystemDirectoryHandle,
	baseName: string,
	recordingUuid: UUID
) => {
	const streamIdToWriteStream = new Map<string, FileSystemWritableFileStream>();
	await DbVideoFragmentTableReader.forRecordingUuid(recordingUuid).forRecordingFragmentsWithAsyncCallback( async (fragment) => {
		const {streamId, data} = fragment;
		let writeStream = streamIdToWriteStream.get(streamId);
		if (writeStream == null) {
			const priorFiles = streamIdToWriteStream.size;
			const fileName = `${fileNameSafeString(baseName)} Video${ priorFiles > 0 ? ` ${priorFiles + 1}` : ''}.webm`;
			const fileSystemHandle = await directoryHandle.getFileHandle(fileName,{ create: true });
			writeStream = await fileSystemHandle.createWritable({ keepExistingData: false });
			streamIdToWriteStream.set(streamId, writeStream);
		}
		await writeStream.write(data);
	});
	for (const writeStream of streamIdToWriteStream.values()) {
		await writeStream.close();
	}
};

export const writeMhtmlCapturesToFiles = async (
	directoryHandle: FileSystemDirectoryHandle,
	baseName: string,
	recordingUuid: UUID
) => {
	const db = await requireEvidenceDatabase();
	await DbMhtmlCaptureTableReader.forRecordingUuid(db, recordingUuid).forMhtmlCapturesAsync( async (mhtmlCapture) => {
		const {timeStamp, url, mhtmlBlob} = mhtmlCapture;
		const urlObj = urlToURL(url);
		const fileNameBase = `${baseName} ${urlObj?.hostname}-${urlObj?.pathname} ${new Date(timeStamp).toISOString()}`;
		const fileName = `${fileNameSafeString(fileNameBase)}.mhtml`;
		const fileSystemHandle = await directoryHandle.getFileHandle(fileName, { create: true });
		const writeStream = await fileSystemHandle.createWritable({ keepExistingData: false });
		await writeStream.write(mhtmlBlob);
		await writeStream.close();
	});
};

export const importRecording = async () => {
	const [fileHandle] = await window.showOpenFilePicker({ types: [{
		description: 'Data Goes Where Archive',
		accept: {'application/json': ['.json']},
		
	}]});
	const file = await fileHandle.getFile();
}

export const saveRecordingToDirectory = async (parentDirectoryHandle: FileSystemDirectoryHandle, recordingUuid: UUID) => {
	const recordingReader = await DbRecordingEntryWriter.fromRecordingUuid(recordingUuid);
	if (recordingReader == null) return;
	const {recording} = recordingReader;
	const recordingName = await recordingReader.getName();
	const directoryHandle = await parentDirectoryHandle.getDirectoryHandle(fileNameSafeString(recordingName), { create: true });
	await Promise.all([
		writeRecordingToArchiveFile(directoryHandle, recordingName, recording),
		writeVideoStreamsToFiles(directoryHandle, recordingName, recordingUuid),
		writeMhtmlCapturesToFiles(directoryHandle, recordingName, recordingUuid),
	]);
}

export const saveRecording = async (recordingUuid: UUID) => {
	const dir = await getDirectoryToSaveInto();
	if (dir != null) {
		await saveRecordingToDirectory(dir, recordingUuid);
	}
}

export const saveRecordingFn = (recordingUuid: UUID) => () => saveRecording(recordingUuid);


import { HarBuilder, HarEvent, isHarEventName } from "@uppajung/hardy-har";
import { DbRecordingEntryWriter } from "./DbRecordingEntryWriter";
import { getDatabase, requireEvidenceDatabase } from "./getDatabase";
import { RecordingCreated } from "../StateEvents";
import { replaceArrayBuffersWithBase64 } from "../../lib/utils/arrayBuffersToBase64";
export const saveToHarFile = async (recordingUuid: UUID) => {
	const startIn = (await ExtensionSettings.get()).settings.archiveDirectoryHandle;
	const recordingName = await (await DbRecordingEntryReader.fromRecordingUuid(recordingUuid))?.getName()
	const suggestedName = recordingName != null ? fileNameSafeString(recordingName).replaceAll('.','-') : undefined;
	const fileSystemFileHandle = await window.showSaveFilePicker({
		startIn,
		suggestedName,
		types: [{description: 'HTTP Archive', accept: {'application/json': ['.har']}}]}
	);
	const harBuilder = new HarBuilder({includeResourcesFromDiskCache: true, includeTextFromResponseBody: true});
	const reader = DbEventsTableReader.forRecordingUuid(recordingUuid);
	await reader.forEvents((event) => {
		if (isHarEventName(event.eventName)) {
			harBuilder.onTypedDebuggerEvent(event.eventName, event.raw as HarEvent<typeof event.eventName>);
		}
	});
	const writeStream = await fileSystemFileHandle.createWritable({ keepExistingData: false,  });
	await writeStream.write(JSON.stringify(harBuilder.getHarArchive(), undefined, "\t"));
	await writeStream.close();
}